/**
 * 장례식장 비용 안내 리드 퍼널의 로그인 화면 컨텍스트.
 *
 * 이 퍼널은 정보 4스텝을 `/match/parlor-requests/[id]` 에서 보여주고, **제출 화면은
 * `/login` 에서 까만 화면을 대체한다**. 기존 폼이 제출 후 까만 화면으로 넘기는 자리에
 * 기획의 제출 화면(게이지 + 조문객 수 + 현재 상황 + CTA 2개)이 들어간다.
 *
 * 그래서 제출 화면이 필요한 값을 여기 담아 넘긴다. `jangjiLoginContext` 와 같은 방식으로
 * **JSON 을 읽지 않는다** - `parlors_detail.json`(2.4MB)을 로그인 번들에서 다시 읽는 대신,
 * 퍼널 페이지의 getStaticProps 가 이미 갖고 있는 값을 완성된 형태로 담는다.
 *
 * 탭 단위(sessionStorage)라 탭을 닫으면 지워지고, 카카오 왕복에도 살아남는다.
 */
const COST_FUNNEL_LOGIN_CONTEXT_KEY = "cost_funnel_login_context";

export interface CostFunnelLoginContext {
  /** 헤더·카카오 CTA 에 쓰는 표시용 이름 (별칭 제거된 값) */
  parlorName: string;
  /** memo 와 `parlor_request_info` 쿠키에 쓰는 원본 companyname */
  companyname: string;
  /** 견적 body 의 `funeral_sido` */
  sido: number;
  /** 견적 body 의 `funeral_sigungu` */
  sigungu: number;
  /** 비용 게이지의 좌우 끝. 만원 단위 (무빈소 ~ 일반장) */
  costRange: { min: number; max: number };
}

/** 퍼널 마지막 스텝에서 `/login` 으로 넘기기 직전에 심는다. */
export const markCostFunnelLogin = (context: CostFunnelLoginContext) => {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(COST_FUNNEL_LOGIN_CONTEXT_KEY, JSON.stringify(context));
  } catch {
    // 사파리 프라이빗 등 쓰기 실패. 마킹이 없으면 로그인 화면이 기존 캐스케이드로 흐른다
  }
};

/**
 * 다른 경로로 제출했으면 지운다. 그 경로의 우선순위대로 화면이 결정되게 한다.
 * (`clearRegionQuoteContext` 와 같은 규칙)
 */
export const clearCostFunnelLoginContext = () => {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(COST_FUNNEL_LOGIN_CONTEXT_KEY);
  } catch {
    // ignore
  }
};

/**
 * 마킹 읽기. 값이 없거나 형태가 어긋나면 null 을 준다(호출부가 기존 화면으로 흐른다).
 *
 * 마킹은 퍼널 마지막 스텝을 통과해야만 생기므로, `/login` 직접 진입이나 쓰기 실패에서
 * 빈 이름·빈 게이지가 뜨지 않는다.
 */
export const getCostFunnelLoginContext = (): CostFunnelLoginContext | null => {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(COST_FUNNEL_LOGIN_CONTEXT_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (typeof parsed?.companyname !== "string" || !parsed.companyname) return null;
    if (typeof parsed?.sido !== "number" || typeof parsed?.sigungu !== "number") return null;
    if (typeof parsed?.costRange?.min !== "number" || typeof parsed?.costRange?.max !== "number") {
      return null;
    }

    return {
      parlorName:
        typeof parsed.parlorName === "string" && parsed.parlorName
          ? parsed.parlorName
          : parsed.companyname,
      companyname: parsed.companyname,
      sido: parsed.sido,
      sigungu: parsed.sigungu,
      costRange: { min: parsed.costRange.min, max: parsed.costRange.max },
    };
  } catch {
    return null;
  }
};
